import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const helpingParam = searchParams.get("helping") === "true";

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile: "",
    family_branch: "",
    location: "",
    interested_attending: "yes",
    which_event: "both",
    num_people: 1,
    want_updates: true,
    interested_helping: helpingParam,
    comments: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error("Please fill in your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/registrations`, {
        ...form,
        num_people: parseInt(form.num_people) || 1,
      });
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-paper">
        <div
          data-testid="registration-success"
          className="max-w-lg mx-auto px-6 text-center py-20"
        >
          <CheckCircle size={48} className="text-forest mx-auto mb-6" />
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-charcoal mb-4">
            Thank You
          </h1>
          <p className="text-lg font-sans leading-relaxed text-charcoal/75 mb-8">
            Thank you for registering your interest — this has been received.
          </p>
          <Link to="/">
            <Button
              data-testid="back-to-home-button"
              variant="outline"
              className="border-forest text-forest hover:bg-forest/5 font-sans text-base px-6 py-5 h-auto"
            >
              <ArrowLeft size={18} className="mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-paper py-10 sm:py-16 lg:py-20">
      <div className="max-w-2xl mx-auto px-5 sm:px-8">
        <Link
          to="/"
          data-testid="register-back-link"
          className="inline-flex items-center text-sm font-sans text-charcoal/60 hover:text-charcoal mb-8 transition-colors"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to Home
        </Link>

        <div className="mb-10">
          <h1
            data-testid="register-heading"
            className="font-serif text-2xl sm:text-4xl font-semibold text-charcoal mb-3"
          >
            Register Your Interest
          </h1>
          <p className="text-[0.95rem] sm:text-lg font-sans text-charcoal/70 leading-relaxed">
            We would love to know you are interested in joining the McCartney
            family gathering in January 2027. Please fill in your details below.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="registration-form"
          className="space-y-6"
        >
          {/* Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name" className="text-base font-sans font-medium text-charcoal">
                First Name *
              </Label>
              <Input
                id="first_name"
                data-testid="input-first-name"
                value={form.first_name}
                onChange={(e) => handleChange("first_name", e.target.value)}
                placeholder="First name"
                className="h-12 text-base bg-paper border-sand focus:ring-forest focus:border-forest"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name" className="text-base font-sans font-medium text-charcoal">
                Last Name *
              </Label>
              <Input
                id="last_name"
                data-testid="input-last-name"
                value={form.last_name}
                onChange={(e) => handleChange("last_name", e.target.value)}
                placeholder="Last name"
                className="h-12 text-base bg-paper border-sand focus:ring-forest focus:border-forest"
                required
              />
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-base font-sans font-medium text-charcoal">
              Email Address *
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="input-email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              placeholder="your@email.com"
              className="h-12 text-base bg-paper border-sand focus:ring-forest focus:border-forest"
              required
            />
          </div>

          {/* Mobile */}
          <div className="space-y-2">
            <Label htmlFor="mobile" className="text-base font-sans font-medium text-charcoal">
              Mobile Number
            </Label>
            <Input
              id="mobile"
              data-testid="input-mobile"
              value={form.mobile}
              onChange={(e) => handleChange("mobile", e.target.value)}
              placeholder="e.g. 021 123 4567"
              className="h-12 text-base bg-paper border-sand focus:ring-forest focus:border-forest"
            />
          </div>

          {/* Family Branch */}
          <div className="space-y-2">
            <Label className="text-base font-sans font-medium text-charcoal">
              Please select the family branch you're connected to, if known.
            </Label>
            <Select
              value={form.family_branch}
              onValueChange={(val) => handleChange("family_branch", val)}
            >
              <SelectTrigger data-testid="select-family-branch" className="h-12 text-base bg-paper border-sand">
                <SelectValue placeholder="Select a family branch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Mary (Maimie)">Mary (Maimie)</SelectItem>
                <SelectItem value="Elizabeth (Cath)">Elizabeth (Cath)</SelectItem>
                <SelectItem value="John (Jack)">John (Jack)</SelectItem>
                <SelectItem value="Margaret (Maggie) Kerse">Margaret (Maggie) Kerse</SelectItem>
                <SelectItem value="Sylvia Wilkins">Sylvia Wilkins</SelectItem>
                <SelectItem value="Other/To Confirm">Other / To Confirm</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location" className="text-base font-sans font-medium text-charcoal">
              Current Location
            </Label>
            <Input
              id="location"
              data-testid="input-location"
              value={form.location}
              onChange={(e) => handleChange("location", e.target.value)}
              placeholder="e.g. Invercargill, NZ"
              className="h-12 text-base bg-paper border-sand focus:ring-forest focus:border-forest"
            />
          </div>

          {/* Attending */}
          <div className="space-y-2">
            <Label className="text-base font-sans font-medium text-charcoal">
              Are you interested in attending?
            </Label>
            <Select
              value={form.interested_attending}
              onValueChange={(val) => handleChange("interested_attending", val)}
            >
              <SelectTrigger data-testid="select-attending" className="h-12 text-base bg-paper border-sand">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="maybe">Maybe — still deciding</SelectItem>
                <SelectItem value="no">No — but want to stay in touch</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Which Event */}
          <div className="space-y-2">
            <Label className="text-base font-sans font-medium text-charcoal">
              Which event are you interested in? (Indicative only)
            </Label>
            <Select
              value={form.which_event}
              onValueChange={(val) => handleChange("which_event", val)}
            >
              <SelectTrigger data-testid="select-event" className="h-12 text-base bg-paper border-sand">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Both — Welcome and Function & Dinner</SelectItem>
                <SelectItem value="welcome">Friday Welcome only</SelectItem>
                <SelectItem value="dinner">Saturday Function & Dinner only</SelectItem>
                <SelectItem value="tbc">To be confirmed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Checkboxes */}
          <div className="space-y-4 pt-2">
            <div className="flex items-center gap-3">
              <Checkbox
                id="want_updates"
                data-testid="checkbox-updates"
                checked={form.want_updates}
                onCheckedChange={(val) => handleChange("want_updates", val)}
                className="border-sand data-[state=checked]:bg-forest data-[state=checked]:border-forest"
              />
              <Label htmlFor="want_updates" className="text-base font-sans text-charcoal/80 cursor-pointer">
                Yes, I'd like to receive updates about the reunion
              </Label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox
                id="interested_helping"
                data-testid="checkbox-helping"
                checked={form.interested_helping}
                onCheckedChange={(val) =>
                  handleChange("interested_helping", val)
                }
                className="border-sand data-[state=checked]:bg-forest data-[state=checked]:border-forest mt-0.5"
              />
              <div>
                <Label htmlFor="interested_helping" className="text-base font-sans text-charcoal/80 cursor-pointer">
                  I'd be open to being contacted about helping in some way
                </Label>
                <p className="text-sm font-sans text-charcoal/50 mt-1">
                  This is simply an expression of interest — there's no obligation.
                </p>
              </div>
            </div>
          </div>

          {/* Comments */}
          <div className="space-y-2">
            <Label htmlFor="comments" className="text-base font-sans font-medium text-charcoal">
              Comments or Notes
            </Label>
            <Textarea
              id="comments"
              data-testid="input-comments"
              value={form.comments}
              onChange={(e) => handleChange("comments", e.target.value)}
              placeholder="Share anything you would like us to know."
              rows={4}
              className="text-base bg-paper border-sand focus:ring-forest focus:border-forest resize-none"
            />
          </div>

          <Button
            type="submit"
            data-testid="register-submit-button"
            disabled={loading}
            className="w-full bg-forest hover:bg-forest/90 text-paper font-sans text-sm sm:text-base py-5 sm:py-6 h-auto rounded-md mt-4"
          >
            {loading ? "Submitting..." : "Register Interest"}
          </Button>
        </form>
      </div>
    </div>
  );
}
